// Minimal Markdown → HTML renderer for MW assistant replies.
//
// Deliberately hand-rolled rather than pulling in a Markdown library: the
// assistant only ever emits the small subset below (headings, rules, tables,
// blockquotes, lists, paragraphs and a few inline marks), and the output is fed
// to dangerouslySetInnerHTML, so every value is HTML-escaped here first.
//
// The PDF export needs the same subset rendered as @react-pdf elements instead
// of HTML - see utils/triloChatPdf.js.

const escapeHtml = s =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const renderInline = s =>
  escapeHtml(s)
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/`(.+?)`/g, "<code>$1</code>")
    .replace(/\^([^\s^]+)\^?/g, "<sup>$1</sup>")
    .replace(/~([^\s~]+)~?/g, "<sub>$1</sub>");

const isTableRow = l => /^\|.+\|$/.test(l.trim());
const isSeparatorRow = l => /^\|[\s\-|:]+\|$/.test(l.trim());

const isBlockStart = l =>
  /^#{1,4}\s/.test(l) ||
  /^---+$/.test(l.trim()) ||
  /^>\s?/.test(l) ||
  /^[-*]\s/.test(l) ||
  /^\d+\.\s/.test(l) ||
  isTableRow(l);

const parseTableRow = l =>
  l
    .trim()
    .replace(/^\||\|$/g, "")
    .split("|")
    .map(c => c.trim());

export function renderMarkdown(text) {
  const inline = renderInline;
  const lines = text.split("\n");
  const out = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (/^#{1,4}\s/.test(line)) {
      const level = line.match(/^(#{1,4})\s/)[1].length;
      out.push(
        `<h${level}>${inline(line.replace(/^#{1,4}\s+/, ""))}</h${level}>`
      );
      i++;
    } else if (/^---+$/.test(line.trim())) {
      out.push("<hr>");
      i++;
    } else if (isTableRow(line)) {
      // Collect all consecutive table lines
      const tableLines = [];
      while (i < lines.length && isTableRow(lines[i])) {
        tableLines.push(lines[i]);
        i++;
      }
      // First row = header, second row = separator (skip), rest = body
      const [headerRow, , ...bodyRows] = tableLines;
      const headers = parseTableRow(headerRow);
      const thead = `<thead><tr>${headers.map(h => `<th>${inline(h)}</th>`).join("")}</tr></thead>`;
      const tbody = bodyRows
        .filter(r => !isSeparatorRow(r))
        .map(
          r =>
            `<tr>${parseTableRow(r)
              .map(c => `<td>${inline(c)}</td>`)
              .join("")}</tr>`
        )
        .join("");
      out.push(`<table>${thead}<tbody>${tbody}</tbody></table>`);
    } else if (/^>\s?/.test(line) && line.trim() !== "") {
      const bqLines = [];
      while (
        i < lines.length &&
        /^>\s?/.test(lines[i]) &&
        lines[i].trim() !== ""
      ) {
        bqLines.push(inline(lines[i].replace(/^>\s?/, "")));
        i++;
      }
      out.push(`<blockquote><p>${bqLines.join("<br>")}</p></blockquote>`);
    } else if (/^[-*]\s/.test(line)) {
      const items = [];
      while (i < lines.length && /^[-*]\s/.test(lines[i])) {
        items.push(`<li>${inline(lines[i].replace(/^[-*]\s+/, ""))}</li>`);
        i++;
      }
      out.push(`<ul>${items.join("")}</ul>`);
    } else if (/^\d+\.\s/.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i])) {
        items.push(`<li>${inline(lines[i].replace(/^\d+\.\s+/, ""))}</li>`);
        i++;
      }
      out.push(`<ol>${items.join("")}</ol>`);
    } else if (line.trim() === "") {
      i++;
    } else {
      const paraLines = [];
      while (
        i < lines.length &&
        lines[i].trim() !== "" &&
        !isBlockStart(lines[i])
      ) {
        paraLines.push(inline(lines[i]));
        i++;
      }
      if (paraLines.length > 0) {
        out.push(`<p>${paraLines.join("<br>")}</p>`);
      } else {
        out.push(`<p>${inline(line)}</p>`);
        i++;
      }
    }
  }

  return out.join("\n");
}
